import React from 'react'
import { isEmpty } from 'lodash'
import { useAPI } from 'contexts/APIProvider'
import { Button, Chip, Typography, Alert, Box, MenuItem } from '@mui/material'
import { AvailabilityCheckResultSearchForm } from 'types/interfaces'
import FormField from 'components/FormField'
import { 
  DataGridPro, 
  GridColDef, 
  GridRenderCellParams, 
  GridValueGetterParams,
  GridEventListener,
} from '@mui/x-data-grid-pro'
import AvailabilityCheckResult from 'logic/AvailabilityCheckResult'
import { AvailabilityCheckStatus } from 'types/types'

interface AdminAvailabilityCheckResultTableProps {
  actions?: React.ReactNode
  onRowClick?: GridEventListener<"rowClick">
  form: AvailabilityCheckResultSearchForm
  updateForm: (name: string, value: any) => void
}

const AdminAvailabilityCheckResultTable: React.FC<AdminAvailabilityCheckResultTableProps> = ({
  actions,
  onRowClick,
  form,
  updateForm,
}) => {
  const { api } = useAPI()
  const [availabilityCheckResults, setAvailabilityCheckResults] = React.useState<AvailabilityCheckResult[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)

  const fetchAvailabilityCheckResults = React.useCallback(async () => {
    setLoading(true)
    setAvailabilityCheckResults(await api.listAvailabilityCheckResults(form))
    setLoading(false)
  }, [api, form])

  React.useEffect(() => {
    fetchAvailabilityCheckResults()
  // only fetch once on page initial load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getStatusColor = (status: AvailabilityCheckStatus) => {
    switch (status) {
      case 'available':
        return 'success'
      case 'unavailable':
        return 'error'
      case 'pending':
        return 'warning'
      default:
        return 'default'
    }
  }

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'good':
        return 'success'
      case 'new':
        return 'info'
      case 'bad':
        return 'error'
      case 'unapproved':
        return 'warning'
      default:
        return 'default'
    }
  }

  const columns: GridColDef<AvailabilityCheckResult>[] = React.useMemo(() => [{
    minWidth: 150,
    headerName: 'Status',
    field: 'status',
    type: 'string',
    renderCell: (params: GridRenderCellParams<AvailabilityCheckResult>) => {
      const result = params.row
      return (
        <Chip
          size='small'
          label={result.status}
          color={getStatusColor(result.status)}
        />
      )
    }
  }, {
    minWidth: 200,
    headerName: 'First Updated At',
    field: 'first_updated_at',
    type: 'dateTime',
    valueGetter: (params: GridValueGetterParams<AvailabilityCheckResult>) => {
      return params.row.first_updated_at?.toJSDate() || null
    },
    renderCell: (params: GridRenderCellParams<AvailabilityCheckResult>) => {
      const result = params.row
      return result.first_updated_at ? (
        <Typography>{result.first_updated_at.setZone(result.nurse_tz).toFormat('yyyy-MM-dd HH:mm a')}</Typography>
      ) : (
        <Typography variant="caption">Not updated</Typography>
      )
    }
  }, {
    minWidth: 100,
    headerName: 'Nurse ID',
    field: 'nurse_id',
    type: 'number',
  }, {
    minWidth: 250,
    headerName: 'Nurse Name',
    field: 'nurse_name',
    type: 'string',
    valueGetter: (params: GridValueGetterParams<AvailabilityCheckResult>) => {
      return `${params.row.nurse_first_name} ${params.row.nurse_last_name}`
    },
    renderCell: (params: GridRenderCellParams<AvailabilityCheckResult>) => {
      const result = params.row
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography>{result.nurse_first_name} {result.nurse_last_name}</Typography>
          <Typography variant="caption">{result.nurse_email}</Typography>
        </Box>
      )
    }
  }, {
    minWidth: 100,
    headerName: 'Nurse Tier',
    field: 'nurse_tier',
    type: 'string',
    renderCell: (params: GridRenderCellParams<AvailabilityCheckResult>) => {
      const result = params.row
      return (
        <Chip
          size='small'
          label={result.nurse_tier}
          color={getTierColor(result.nurse_tier)}
        />
      )
    }
  }, {
    minWidth: 100,
    headerName: 'Distance',
    field: 'distance_km',
    type: 'number',
    renderCell: (params: GridRenderCellParams<AvailabilityCheckResult>) => {
      return params.row.distance_km ? `${params.row.distance_km.toFixed(2)} km` : 'N/A'
    }
  }, {
    minWidth: 250,
    headerName: 'Nurse Approvals',
    field: 'nurse_approvals',
    type: 'string',
    renderCell: (params: GridRenderCellParams<AvailabilityCheckResult>) => {
      const result = params.row
      const approvals = []
      if (result.nurse_approved) approvals.push('Approved')
      if (result.nurse_extended_compliance_approved) approvals.push('Extended')
      if (result.nurse_hygienist_approved) approvals.push('Hygienist')
      if (result.nurse_sedation_approved) approvals.push('Sedation')
      if (result.nurse_radiography_approved) approvals.push('Radiography')
      
      return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1}}>
          {approvals.map(approval => (
            <Chip
              key={approval}
              size='small'
              label={approval}
              color='success'
            />
          ))}
        </Box>
      )
    },
  }, {
    minWidth: 250,
    headerName: 'Nurse Experience',
    field: 'experience',
    type: 'string',
    renderCell: (params: GridRenderCellParams<AvailabilityCheckResult>) => {
      const result = params.row
      const experiences = []
      if (result.nurse_implant_experience) experiences.push(`Implant: ${result.nurse_implant_experience}`)
      if (result.nurse_orthodontics_experience) experiences.push(`Orthodontics: ${result.nurse_orthodontics_experience}`)
      
      return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1}}>
          {experiences.map(experience => (
            <Chip
              key={experience}
              size='small'
              label={experience}
              color='info'
            />
          ))}
        </Box>
      )
    },
  }], [])

  const statusOptions: AvailabilityCheckStatus[] = ['available', 'unavailable', 'pending']

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
        <FormField
          name='availability_check_id'
          label='Check ID'
          type='number'
          onChange={(e) => updateForm('availability_check_id', e.target.value ? Number(e.target.value) : undefined)}
          value={form.availability_check_id?.toString() ?? ''}
        />
        <FormField
          name='nurse_id'
          label='Nurse ID'
          type='number'
          onChange={(e) => updateForm('nurse_id', e.target.value ? Number(e.target.value) : undefined)}
          value={form.nurse_id?.toString() ?? ''}
        />
        <FormField
          select
          sx={{ width: 150 }}
          name='status'
          label='Status'
          onChange={(e) => updateForm('status', e.target.value === '' ? undefined : e.target.value)}
          value={form.status ?? ''}>
          <MenuItem value=''>All</MenuItem>
          {statusOptions.map(status => (
            <MenuItem key={status} value={status}>{status}</MenuItem>
          ))}
        </FormField>
        <FormField
          select
          sx={{ width: 150 }}
          name='is_expired'
          label='Expired'
          onChange={(e) => updateForm('is_expired', e.target.value === '' ? undefined : e.target.value === 'true')}
          value={form.is_expired === undefined ? '' : form.is_expired.toString()}>
          <MenuItem value=''>All</MenuItem>
          <MenuItem value='false'>No</MenuItem>
          <MenuItem value='true'>Yes</MenuItem>
        </FormField>
        <Button
          variant="contained"
          onClick={fetchAvailabilityCheckResults}
          disabled={loading}>
          {loading ? 'Please Wait ...' : 'Search'}
        </Button>
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {actions}
        </Box>
      </Box>
      {isEmpty(availabilityCheckResults) ? (
        <Alert severity='info'>No Records Yet</Alert>
      ) : (
        <React.Fragment>
          <Alert severity='info'>{availabilityCheckResults.length} records returned.</Alert>
          <DataGridPro 
            rows={availabilityCheckResults}
            columns={columns}
            getRowId={(result) => result.id}
            disableRowSelectionOnClick
            loading={loading}
            onRowDoubleClick={onRowClick}
            hideFooter
          />
        </React.Fragment>
      )}
    </Box>
  )
}

export default AdminAvailabilityCheckResultTable
